import type { FieldSuggestionItem } from '@dealstack/frontend/shared/components/DocumentEditor/extensions/fieldSuggestion.ts';
import type { OfferedFields } from '@dealstack/frontend/shared/components/DocumentEditor/dataPointFields.ts';
import { uniqueFieldName } from '@dealstack/frontend/shared/components/DocumentEditor/fieldNameFromPath.ts';
import { fieldIdentity } from '@dealstack/shared/lib/documents/scopedFieldNames.ts';
import type { ScopedPaletteField } from '@dealstack/frontend/shared/components/FieldBinding/BindPanel/BindPanel.types.ts';

/**
 * Folds the pinned security's scoped fields into what `offeredFields` already produced.
 *
 * Scoped fields deliberately skip `offeredFields`'s own name derivation: a scoped path carries
 * three Liquid filters, which that derivation collapses to `holdings.units_held` no matter which
 * security is pinned — the exact collision the requester rejected. They arrive already named by
 * `scopedFieldName`, which folds in the security and the source. Only the dedup half of
 * `offeredFields` — the `taken` set and `uniqueFieldName` suffixing — is still shared, so a
 * scoped name can't collide with a sibling scoped field and can't silently shadow a catalogue
 * one.
 *
 * `existing` is what the DOCUMENT already holds (name → binding, undefined when unlinked), the
 * same map `offeredFields` dedups against. Seeding `taken` from the catalogue's items alone left
 * every document-only name out of it, so a field an author had hand-typed as
 * `{{ a_ordinary_shares.holding.units_held }}` in Word — or a .docx import carrying one — took
 * the scoped binding over whatever it was bound to. The name is REUSED when the document's field
 * is unlinked or already bound to this same expression (both benign, and the second is the
 * reopen case); it is suffixed only when the document has it bound to something else.
 */
export const mergeScopedFields = (
	offered: OfferedFields,
	scoped: readonly ScopedPaletteField[],
	existing: ReadonlyMap<string, string | undefined>
): OfferedFields => {
	const catalogueNames = new Set([
		...offered.items.map((item) => item.name),
		...Object.keys(offered.withheld),
	]);
	const taken = new Set([...existing.keys(), ...catalogueNames]);
	const items: FieldSuggestionItem[] = [...offered.items];
	const bindings: Record<string, string> = { ...offered.bindings };
	// The document's own names by identity: renaming a security changes the name this mints, and
	// offering the new one beside the placed field gave two indistinguishable entries.
	const documentNameByIdentity = new Map<string, string>();
	for (const name of existing.keys()) {
		const identity = fieldIdentity(name);
		if (identity !== null && !documentNameByIdentity.has(identity)) {
			documentNameByIdentity.set(identity, name);
		}
	}

	for (const field of scoped) {
		const identity = fieldIdentity(field.name);
		const placedAs = identity === null ? undefined : documentNameByIdentity.get(identity);
		if (placedAs !== undefined && placedAs !== field.name) {
			// Same data point on the same security, under the name it was placed with.
			taken.add(placedAs);
			bindings[placedAs] = field.binding;
			items.push({
				name: placedAs,
				label: field.label,
				group: field.group,
				...(field.sampleValue === '' ? {} : { sample: field.sampleValue }),
				...(field.firstOfList ? { firstOfList: true } : {}),
			});
			continue;
		}
		const inDocument = existing.get(field.name);
		const reusable =
			!taken.has(field.name) ||
			(!catalogueNames.has(field.name) &&
				existing.has(field.name) &&
				(inDocument === undefined || inDocument === field.binding));
		const name = reusable ? field.name : uniqueFieldName(field.name, taken);
		taken.add(name);
		bindings[name] = field.binding;
		items.push({
			name,
			label: field.label,
			group: field.group,
			...(field.sampleValue === '' ? {} : { sample: field.sampleValue }),
			// Forwarded, not dropped: an allocation field is a `| first` over a list that can
			// hold two rows for one security, and the label cannot say so.
			...(field.firstOfList ? { firstOfList: true } : {}),
		});
	}

	return { items, bindings, withheld: offered.withheld };
};
